import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'features/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { ACTION_TYPES, ROUTES } from 'utils';
import { Open } from 'types';
import { useWidget, useVote, VoteAPIResponseCode } from '@telescope/cassini-hooks';
import { ErrorType } from 'types';
import { isTrue } from 'utils';

export interface SpinToWinContextValue {
  isAnimating: boolean
  handleExtraSpin: () => void
  handleSpinToAuth: () => void
  onAnimationComplete: () => void
  displayResults: boolean
  spinsRemaining: number
  isMultiSpin: boolean
  voteLimit: number
  prizeId: string | undefined
}

const SpinToWinContext = createContext<SpinToWinContextValue | undefined>(undefined);

const AUTH_DELAY = 2500;
const RESULTS_DELAY = 800;

export const SpinToWinProvider = ({ children }: { children: React.ReactElement }) => {

  const {
    data: rulesetParameters,
  } = useWidget({ select: (data: Open) => data.snapshot.snapshotSettings.settings.ruleset_parameters  });
  const { voteLimit = Infinity } = rulesetParameters!
  
  const vote = useVote();
  
  const { user, updateUserVoteHistory } = useAuth();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPendingAuth, setIsPendingAuth] = useState(false);
  const [displayResults, setDisplayResults] = useState(false);

  const [prizeId, setPrizeId] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const location = useLocation();

  const handleExtraSpin = async () => {
    try {
      
      const { response_code, is_winner, prize_data, prize_code, prize_name, prize_id, wip_votestring: votestring } = await vote({
        action_type: ACTION_TYPES.SWEEPS,
        ...user!.user
      });

      if (response_code !== VoteAPIResponseCode.VALID) {
        const errors = {
          [VoteAPIResponseCode.WINDOW]: ErrorType.WINDOW,
          [VoteAPIResponseCode.OVERLIMIT]: ErrorType.OVERLIMIT,
          [VoteAPIResponseCode.GENERAL_INVALID]: ErrorType.GENERIC,
        };
        const error = errors[response_code as keyof typeof errors] || errors[VoteAPIResponseCode.GENERAL_INVALID];

        return navigate(`${ROUTES.ERROR}/${error}`);
      }
      
      const prize = {
        code: prize_code,
        name: prize_name,
        id: prize_id,
        ...(prize_data && {prize_data: JSON.parse(prize_data)})
      }

      await updateUserVoteHistory({ isWinner: isTrue(is_winner), prize, votestring });
      
      handleSpinToPrize(prize.id);
      setDisplayResults(false);

    } catch(error) {
      return navigate(`${ROUTES.ERROR}/${ErrorType.GENERIC}`);
    }
  }

  const handleSpinToPrize = useCallback((prizeId: string | undefined) => {
    setPrizeId(prizeId);
    setIsAnimating(true);
  }, [user]);

  useEffect(() => {
    if (!user?.user) {
      setDisplayResults(false);
      return;
    }

    if (!isPendingAuth) return;

    setIsPendingAuth(false);
    handleSpinToPrize(user?.prize?.id);

  }, [user?.user, isPendingAuth])

  const handleSpinToAuth = useCallback(() => {
    setPrizeId(undefined);
    setIsPendingAuth(true);
    setIsAnimating(true);

    setTimeout(() => {
      navigate(ROUTES.LOGIN, { state: { backgroundLocation: location } })
      setIsAnimating(false);
    }, AUTH_DELAY)
  }, []);

  const onAnimationComplete = useCallback(() => {
    // Let wheel sit on winning prize for a second before switching to results
    setTimeout(() => {
      setIsAnimating(false);
      setDisplayResults(true);
    }, RESULTS_DELAY)
  }, []);

  const value = useMemo(() => ({
    isAnimating,
    prizeId,
    displayResults,
    spinsRemaining: user? voteLimit - user.votestring : voteLimit,
    isMultiSpin: voteLimit > 1,
    voteLimit,
    handleSpinToAuth,
    handleExtraSpin,
    onAnimationComplete
  }), [
    isAnimating,
    prizeId,
    displayResults,
    handleSpinToAuth,
    handleExtraSpin,
    onAnimationComplete,
    user?.votestring,
    voteLimit
  ]);

  return <SpinToWinContext.Provider value={value}>{children}</SpinToWinContext.Provider>;
};

export function useSpinToWin() {
  const context = useContext(SpinToWinContext);

  if (context === undefined) {
    throw new Error('useSpinToWin must be used within a SpinToWinProvider');
  }

  return context;
}